/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";

const TAG: string = "reloadApplicationAtRoot";

/**
 * Restarts the app at `/`, keeping the query string — so the shell's
 * `x-app-version` survives and is written back to its cookie on the next
 * boot — and dropping the path.
 *
 * Every recovery reload goes here rather than to `location.reload()`, because
 * the path the app is sitting on is not a safe place to reload:
 *
 *   - **Its assets may not resolve.** `output.publicPath` is webpack's default
 *     "auto", so HtmlWebpackPlugin emits *relative* script tags
 *     (`src="main.js"`). A document served at `/select-profile` therefore
 *     requests `/select-profile/main.js`, which 404s — no script runs at all
 *     and the screen stays black. Verified against the dev server. At `/` the
 *     relative tags resolve, and webpack then derives its own chunk base URL
 *     from the script it loaded.
 *   - **It may not be served at all.** Deep paths depend on the SPA fallback
 *     (CloudFront 403/404 → `/index.html`), still listed as pending
 *     infrastructure in docs/application-routing-plan.md §Phase 3.
 *   - **It always matches a route** (splash), so a URL that matched nothing
 *     cannot reproduce its own 404, and the app routes forward from startup.
 *
 * The screen the user was on is lost, which is the right trade for a recovery:
 * that screen is precisely what failed to load.
 *
 * `replace` rather than `assign`: a URL that just failed is not worth a history
 * entry, and leaving it there would let Back walk straight back into it.
 *
 * Its own module partly to be a seam — `window.location` is non-configurable in
 * jsdom, so anything that navigates inline cannot be asserted in a test. The
 * recovery logic that decides *whether* to reload mocks this module instead.
 */
export function reloadApplicationAtRoot(): void {
  const target: string = window.location.origin + "/" + window.location.search;
  Log.i(TAG, "Restarting application at root: " + target);
  window.location.replace(target);
}
